import React from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faTrophy,
  faGlobe,
  faEarthAmericas,
} from "@fortawesome/free-solid-svg-icons";
import VerificationBadge from "../user/VerificationBadge";

/**
 * GlobalLeagueCard component for showing global league information
 * Mobile-first design with consistent spacing and touch-friendly interactions
 */
const GlobalLeagueCard = ({
  league = null,
  leaders = [],
  totalParticipants = 0,
  userGlobalEntries = 0,
  loading = false,
  buttonText = "View Global Competition",
  onButtonClick,
  buttonIcon = faGlobe,
  buttonVariant = "primary",
  buttonSize = "md",
}) => {
  const navigate = useNavigate();

  // Handle click navigation to user profile
  const handleUserClick = (leader, e) => {
    e.preventDefault();
    e.stopPropagation();
    const userId = leader.userId || leader.user?._id;
    if (userId) {
      navigate(`/users/${userId}`);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-dark-grey to-gray-700 p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center">
            <img
              src="/emblem-gold.png"
              alt="DayShift Emblem"
              className="w-10 h-10 object-contain"
            />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              Global Competition
              <FontAwesomeIcon
                icon={faEarthAmericas}
                className="text-gold w-4 h-4"
              />
            </h2>
            <p className="text-white/80 text-sm">
              Compete against MMA fans worldwide
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-deep-red/10 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faUsers}
                  className="text-deep-red w-4 h-4"
                />
              </div>
            </div>
            <p className="text-xs text-medium-grey mb-1">Participants</p>
            <p className="text-lg font-bold text-dark-grey">
              {loading
                ? "-"
                : totalParticipants > 0
                  ? totalParticipants.toLocaleString()
                  : "0"}
            </p>
          </div>

          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-deep-red/10 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faTrophy}
                  className="text-deep-red w-4 h-4"
                />
              </div>
            </div>
            <p className="text-xs text-medium-grey mb-1">Your Entries</p>
            <p className="text-lg font-bold text-dark-grey">
              {loading ? "-" : userGlobalEntries}
            </p>
          </div>
        </div>

        {/* Leaders Section */}
        {leaders.length > 0 && (
          <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
            <div className="p-3 flex items-center justify-between border-b border-gray-200">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faTrophy}
                  className="text-gold w-4 h-4"
                />
                <p className="text-sm font-bold text-dark-grey">Top Leaders</p>
              </div>
              <span className="text-xs text-medium-grey">Score</span>
            </div>
            <div className="divide-y divide-gray-200">
              {leaders.slice(0, 3).map((leader, index) => (
                <div
                  key={leader.userId || index}
                  className="flex justify-between items-center p-3"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                      ${
                        index === 0
                          ? "bg-gold text-dark-grey"
                          : index === 1
                            ? "bg-gray-400 text-white"
                            : "bg-gray-600 text-white"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        onClick={(e) => handleUserClick(leader, e)}
                        className="text-sm text-dark-grey hover:text-deep-red hover:underline cursor-pointer truncate font-medium"
                        title={`View ${
                          leader.username ||
                          leader.user?.username ||
                          leader.displayName ||
                          leader.user?.name ||
                          "User"
                        }'s profile`}
                      >
                        {leader.username ||
                          leader.user?.username ||
                          leader.displayName ||
                          leader.user?.name ||
                          "User"}
                      </button>
                      {(leader.verified || leader.user?.verified) && (
                        <VerificationBadge
                          verified={leader.verified || leader.user?.verified}
                          size="small"
                          showTooltip={true}
                        />
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-dark-grey">
                    {leader.points ? leader.points.toFixed(2) : "0.00"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={onButtonClick}
          className="w-full bg-deep-red text-white py-3 rounded-xl text-sm font-bold hover:bg-bright-red transition-colors min-h-[44px] flex items-center justify-center gap-2"
        >
          {buttonText}
          <FontAwesomeIcon icon={buttonIcon} className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default GlobalLeagueCard;
